import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectTopicChoices } from "../../features/training/training-slice";
import { selectOccurrenceCancelled } from "../../features/sessions/visits-slice";
import {
  fetchOccurrenceTopic,
  fetchTopicChoices,
  setOccurrenceTopic,
} from "../../features/training/slice-extensions/training-topics-slice-extension";
import { OccurrenceTopic } from "../../features/training/models/TopicPlan";
import { OccurrenceTopicSource } from "../../features/training/contracts/OccurrenceTopicSource";

interface OccurrenceTopicSelectProps {
  sessionId: string;
  /** yyyy-MM-dd, the attendance date key */
  date: string;
}

const SOURCE_KEY: Record<OccurrenceTopicSource, string> = {
  Plan: "training.occurrenceTopic.fromPlan",
  Frozen: "training.occurrenceTopic.frozen",
  Override: "training.occurrenceTopic.override",
};

/**
 * The topic one Session date teaches, on the attendance page. Changing it sets an
 * override for this date only; the rest of the rotation is untouched. On a date the
 * instructor cancelled the topic is shown read-only, like the attendance record.
 */
const OccurrenceTopicSelect: React.FC<OccurrenceTopicSelectProps> = ({ sessionId, date }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const choices = useAppSelector(selectTopicChoices);
  const cancelled = useAppSelector(selectOccurrenceCancelled(sessionId, date));
  const [current, setCurrent] = useState<OccurrenceTopic | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchTopicChoices());
  }, [dispatch]);

  useEffect(() => {
    let active = true;
    dispatch(fetchOccurrenceTopic({ sessionId, date })).then((result) => {
      if (active && fetchOccurrenceTopic.fulfilled.match(result)) setCurrent(result.payload);
    });
    return () => {
      active = false;
    };
  }, [dispatch, sessionId, date]);

  if (choices.length === 0 && !current) return null;
  if (cancelled) {
    if (!current) return null;
    return (
      <div className="label">
        <span className="label-text">{t("training.occurrenceTopic.label")}</span>
        <p className="text-grey-20 dark:text-white">{`${current.topic.code} · ${current.topic.title}`}</p>
      </div>
    );
  }

  const handleChange = async (topicId: string) => {
    setBusy(true);
    setError(null);
    const result = await dispatch(
      setOccurrenceTopic({ sessionId, date, request: { topicId: topicId || null } })
    );
    setBusy(false);
    if (setOccurrenceTopic.fulfilled.match(result)) setCurrent(result.payload);
    else setError(result.payload?.message ?? t("training.occurrenceTopic.saveFailed"));
  };

  // A recorded topic that is no longer offered (archived) still shows as the current value.
  const options = current && !choices.some((c) => c.topicId === current.topic.topicId)
    ? [current.topic, ...choices]
    : choices;

  return (
    <label className="label">
      <span className="label-text">{t("training.occurrenceTopic.label")}</span>
      <select
        className="input w-full sm:w-80"
        value={current?.topic.topicId ?? ""}
        disabled={busy}
        onChange={(e) => handleChange(e.target.value)}
      >
        <option value="">{t("training.occurrenceTopic.none")}</option>
        {options.map((c) => (
          <option key={c.topicId} value={c.topicId}>
            {`${c.code} · ${c.title}`}
          </option>
        ))}
      </select>
      {current && (
        <span className="text-xs text-grey-40 dark:text-grey-80">{t(SOURCE_KEY[current.source])}</span>
      )}
      {error && <span className="text-sm text-red-600 dark:text-red-400">{error}</span>}
    </label>
  );
};

export default OccurrenceTopicSelect;
